import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { User } from 'src/app/_types/auth.types';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';

export type CancellationDetailsState = {
  isLoading: boolean;
  details: CancellationFlowDetailsResponse | null;
  userProfile: User | null;
  /** The requested id matches nothing — a stale link, not a transient failure. */
  notFound: boolean;
  failed: boolean;
};

const INITIAL_STATE: CancellationDetailsState = {
  isLoading: false,
  details: null,
  userProfile: null,
  notFound: false,
  failed: false,
};

/**
 * Mediator of the cancellation modal, the way `TaskDetailsService` is of the
 * flow's: one instance per modal, provided by it, read by its sections. The
 * sections read `state` in their getters, so change detection picks up every
 * update without a subscription.
 *
 * Loads the cancellation, then the customer it belongs to; a late answer to
 * an earlier request is dropped, so switching cancellations never paints a
 * stale one. A cancellation card on the affaire provides its own instance
 * and `seed`s it from the affaire's batch instead: nothing is loaded.
 */
@Injectable()
export class CancellationDetailsService {
  private readonly cancellationService = inject(CancellationService);

  private readonly userAuthService = inject(UserAuthService);

  private currentState: CancellationDetailsState = INITIAL_STATE;

  private lastRequestedId: string | null = null;

  get state(): CancellationDetailsState {
    return this.currentState;
  }

  clear(): void {
    this.lastRequestedId = null;
    this.currentState = INITIAL_STATE;
  }

  /**
   * A link answered with the refreshed aggregate: taken as is, sparing a
   * round trip — unless another cancellation was requested meanwhile.
   */
  replaceDetails(details: CancellationFlowDetailsResponse): void {
    if (details.id !== this.lastRequestedId) return;
    this.updateState({ details });
  }

  /** Details the host already holds, taken as loaded: no request, no customer. */
  seed(details: CancellationFlowDetailsResponse): void {
    this.lastRequestedId = details.id;
    this.currentState = { ...INITIAL_STATE, details };
  }

  load(cancellationId: string): void {
    this.lastRequestedId = cancellationId;
    this.updateState({ isLoading: true, notFound: false, failed: false });

    this.cancellationService.getCancellationFlow(cancellationId).subscribe({
      next: (res) => {
        if (this.lastRequestedId !== cancellationId) return;
        const details = res.data ?? null;
        if (!details) {
          this.updateState({ isLoading: false, details: null, notFound: true });
          return;
        }
        this.updateState({ details });
        this.userAuthService.getUserDetails(details.userId).subscribe({
          next: (userRes) => {
            if (this.lastRequestedId !== cancellationId) return;
            this.updateState({
              userProfile: userRes?.data ?? null,
              isLoading: false,
            });
          },
          error: () => {
            if (this.lastRequestedId !== cancellationId) return;
            this.updateState({ userProfile: null, isLoading: false });
          },
        });
      },
      error: (err: HttpErrorResponse) => {
        if (this.lastRequestedId !== cancellationId) return;
        this.updateState({
          isLoading: false,
          details: null,
          userProfile: null,
          notFound: err.status === 404,
          failed: err.status !== 404,
        });
      },
    });
  }

  private updateState(partial: Partial<CancellationDetailsState>): void {
    this.currentState = { ...this.currentState, ...partial };
  }
}
